// pages/Movimentos/Movimentos.js - VERSÃO COM FILTRO POR ARMAZÉM

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Movimentos.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

// ==============================================
// CONFIGURAÇÕES PADRÃO (fallback)
// ==============================================
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.'
    },
    formatoData: 'DD/MM/YYYY',
    timezone: 'Africa/Maputo',
    idioma: 'pt-PT',
    iva: 16
};

export default function Movimentos() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [movimentos, setMovimentos] = useState([]);
    const [filteredMovimentos, setFilteredMovimentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [configEmpresa, setConfigEmpresa] = useState(CONFIG_PADRAO);

    // ==============================================
    // BARRA DE PESQUISA
    // ==============================================
    const [searchTerm, setSearchTerm] = useState('');

    // ==============================================
    // MODAIS
    // ==============================================
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [movimentoSelecionado, setMovimentoSelecionado] = useState(null);

    // ==============================================
    // FILTROS
    // ==============================================
    const [filtros, setFiltros] = useState({
        tipo: 'todos',
        produtoId: 'todos',
        armazem: 'todos', // 🔥 NOVO FILTRO POR ARMAZÉM
        dataInicio: '',
        dataFim: ''
    });
    const [showFiltros, setShowFiltros] = useState(false);

    // ==============================================
    // DADOS AUXILIARES
    // ==============================================
    const [produtos, setProdutos] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [armazens, setArmazens] = useState([]); // 🔥 NOVO ESTADO PARA ARMAZÉNS

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 5,
        total: 0,
        pages: 0
    });

    // ==============================================
    // ESTADOS ADICIONAIS
    // ==============================================
    const [message, setMessage] = useState({ text: '', type: '' });
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);
    const [periodoStats, setPeriodoStats] = useState('mes');

    // ==============================================
    // API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json'
        }
    });

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const showToast = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    const formatarMoeda = (valor) => {
        const moeda = configEmpresa?.moeda || CONFIG_PADRAO.moeda;
        const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        let numero = Number(valor);
        if (isNaN(numero)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        numero = numero.toFixed(casasDecimais);
        const partes = numero.split('.');
        let inteiros = partes[0];
        const decimais = partes[1] || '0'.repeat(casasDecimais);

        inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

        return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
    };

    const formatarNumero = (valor) => {
        if (valor === undefined || valor === null) return '0';
        const num = Number(valor);
        if (isNaN(num)) return '0';

        if (num >= 1e9) {
            return (num / 1e9).toFixed(1) + 'B';
        }
        if (num >= 1e6) {
            return (num / 1e6).toFixed(1) + 'M';
        }
        if (num >= 1e3) {
            return (num / 1e3).toFixed(1) + 'k';
        }

        return num.toLocaleString('pt-PT');
    };

    const formatarData = (data) => {
        if (!data) return '-';
        const d = new Date(data);
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();
        const horas = String(d.getHours()).padStart(2, '0');
        const minutos = String(d.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
    };

    const getTipoLabel = (tipo) => {
        const labels = {
            entrada: 'Entrada',
            saida: 'Saída',
            ajuste: 'Ajuste'
        };
        return labels[tipo] || tipo;
    };

    const getTipoIcon = (tipo) => {
        const icons = {
            entrada: 'arrow_downward',
            saida: 'arrow_upward',
            ajuste: 'swap_vert'
        };
        return icons[tipo] || 'help';
    };

    const getTipoColor = (tipo) => {
        const colors = {
            entrada: '#10b981',
            saida: '#ef4444',
            ajuste: '#f59e0b'
        };
        return colors[tipo] || '#6b7280';
    };

    const getNomeFornecedor = (fornecedorId, produto) => {
        if (fornecedorId) {
            const fornecedor = fornecedores.find(f => f._id === fornecedorId);
            return fornecedor ? fornecedor.nome : 'Fornecedor não encontrado';
        }

        if (produto && produto.fornecedorId) {
            if (typeof produto.fornecedorId === 'object' && produto.fornecedorId.nome) {
                return produto.fornecedorId.nome;
            }
            if (typeof produto.fornecedorId === 'string') {
                const fornecedor = fornecedores.find(f => f._id === produto.fornecedorId);
                return fornecedor ? fornecedor.nome : 'Fornecedor não encontrado';
            }
        }

        return '-';
    };

    // 🔥 FUNÇÃO PARA OBTER NOME DO ARMAZÉM
    const getNomeArmazem = (armazemId) => {
        if (!armazemId) return '-';
        const armazem = armazens.find(a => a._id === armazemId);
        return armazem ? armazem.nome : 'Armazém não encontrado';
    };

    // ==============================================
    // FETCH DADOS
    // ==============================================
    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                    setEmpresaId(empresaIdExtraido);
                    return storedUser;
                }
                return null;
            }

            const response = await axios.get(`${API_BASE_URL}/auth/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(userData);
                setEmpresaId(empresaIdExtraido);
                localStorage.setItem('usuario', JSON.stringify(userData));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
            const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
            if (storedUser && storedUser._id) {
                setLoggedUser(storedUser);
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                setEmpresaId(empresaIdExtraido);
                return storedUser;
            }
        }
        return null;
    };

    const fetchFornecedores = async () => {
        try {
            const response = await api.get('/fornecedores');
            if (response.data && response.data.data) {
                setFornecedores(response.data.data);
                console.log(`📦 ${response.data.data.length} fornecedores carregados`);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar fornecedores:', err);
        }
    };

    // 🔥 FUNÇÃO PARA BUSCAR ARMAZÉNS
    const fetchArmazens = async () => {
        try {
            console.log('🔍 Buscando armazéns para Movimentos...');
            const response = await api.get('/armazens');

            if (response.data && response.data.success) {
                const armazensData = response.data.data || [];
                setArmazens(armazensData);
                console.log('🏢 Armazéns carregados:', armazensData.length);
            } else {
                console.warn('⚠️ Resposta da API sem dados de armazéns:', response.data);
                setArmazens([]);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar armazéns:', err);
            setArmazens([]);
        }
    };

    const fetchMovimentos = async () => {
        setLoading(true);
        try {
            const response = await api.get('/produtos');

            if (response.data && response.data.data) {
                if (response.data.configuracao) {
                    const config = {
                        ...CONFIG_PADRAO,
                        ...response.data.configuracao,
                        moeda: {
                            ...CONFIG_PADRAO.moeda,
                            ...(response.data.configuracao.moeda || {})
                        }
                    };
                    setConfigEmpresa(config);
                }

                const produtos = response.data.data;
                let todosMovimentos = [];

                produtos.forEach(produto => {
                    // 🔥 PRIMEIRO: Verificar se o produto tem armazemId
                    const produtoArmazemId = produto.armazemId ?
                        (typeof produto.armazemId === 'object' ? produto.armazemId._id : produto.armazemId) :
                        null;

                    const produtoArmazemNome = produto.armazemId ?
                        (typeof produto.armazemId === 'object' ? produto.armazemId.nome : null) :
                        null;

                    if (produto.historicoEntradas && produto.historicoEntradas.length > 0) {
                        produto.historicoEntradas.forEach(entry => {
                            const isEntrada = entry.quantidade > 0;

                            // 🔥 VERIFICAR se o entry tem armazemId, senão usar o do produto
                            let armazemId = null;
                            let armazemNome = null;

                            if (entry.armazemId) {
                                armazemId = typeof entry.armazemId === 'object' ? entry.armazemId._id : entry.armazemId;
                                armazemNome = typeof entry.armazemId === 'object' ? entry.armazemId.nome : null;
                            } else if (entry.armazemNome) {
                                armazemNome = entry.armazemNome;
                            } else if (produtoArmazemId) {
                                armazemId = produtoArmazemId;
                                armazemNome = produtoArmazemNome;
                            }

                            todosMovimentos.push({
                                _id: entry._id || `${produto._id}_${Date.now()}_${Math.random()}`,
                                tipo: isEntrada ? 'entrada' : 'saida',
                                produtoId: {
                                    _id: produto._id,
                                    nome: produto.nome,
                                    codigo: produto.codigo,
                                    unidadeMedida: produto.unidadeMedida,
                                    fornecedorId: produto.fornecedorId || null,
                                    armazemId: produtoArmazemId,
                                    armazemNome: produtoArmazemNome
                                },
                                quantidade: Math.abs(entry.quantidade),
                                precoUnitario: entry.precoUnitario || 0,
                                valorTotal: Math.abs(entry.quantidade) * (entry.precoUnitario || 0),
                                fornecedorId: entry.fornecedorId || null,
                                armazemId: armazemId, // 🔥 CORRIGIDO
                                armazemNome: armazemNome || entry.armazemNome || produtoArmazemNome || null, // 🔥 CORRIGIDO
                                notaFiscal: entry.notaFiscal || null,
                                dataMovimento: entry.dataEntrada || entry.criadoEm || produto.updatedAt || produto.createdAt,
                                observacao: entry.observacao || (isEntrada ? 'Entrada de stock' : 'Saída de stock'),
                                usuarioId: entry.criadoPor || null,
                                status: 'confirmado',
                                stockAntes: 0,
                                stockDepois: 0
                            });
                        });
                    }
                });

                // 🔥 APLICAR FILTROS (INCLUINDO ARMAZÉM)
                let resultado = todosMovimentos;

                if (filtros.tipo && filtros.tipo !== 'todos') {
                    resultado = resultado.filter(m => m.tipo === filtros.tipo);
                }

                if (filtros.produtoId && filtros.produtoId !== 'todos') {
                    resultado = resultado.filter(m => m.produtoId._id === filtros.produtoId);
                }

                // 🔥 NOVO FILTRO POR ARMAZÉM
                if (filtros.armazem && filtros.armazem !== 'todos') {
                    resultado = resultado.filter(m => {
                        // Verificar se o armazemId do movimento é igual ao filtro
                        const armId = m.armazemId ?
                            (typeof m.armazemId === 'object' ? m.armazemId._id : m.armazemId) :
                            null;
                        return armId === filtros.armazem;
                    });
                }

                if (filtros.dataInicio) {
                    const dataInicio = new Date(filtros.dataInicio);
                    resultado = resultado.filter(m => new Date(m.dataMovimento) >= dataInicio);
                }

                if (filtros.dataFim) {
                    const dataFim = new Date(filtros.dataFim);
                    dataFim.setHours(23, 59, 59);
                    resultado = resultado.filter(m => new Date(m.dataMovimento) <= dataFim);
                }

                resultado.sort((a, b) => new Date(b.dataMovimento) - new Date(a.dataMovimento));

                setMovimentos(resultado);
                aplicarPesquisaNaTabela(resultado, searchTerm);
                calcularStats(resultado);

                setPagination(prev => ({
                    ...prev,
                    total: resultado.length,
                    pages: Math.ceil(resultado.length / prev.limit)
                }));
            }
        } catch (err) {
            console.error('❌ Erro ao carregar movimentos:', err);
            showToast('Erro ao carregar movimentos', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const aplicarPesquisaNaTabela = (dados, termo) => {
        if (!dados || dados.length === 0) {
            setFilteredMovimentos([]);
            return;
        }

        if (!termo || termo.trim() === '') {
            setFilteredMovimentos(dados);
            return;
        }

        const term = termo.toLowerCase().trim();
        const resultado = dados.filter(m =>
            m.produtoId?.nome?.toLowerCase().includes(term) ||
            m.produtoId?.codigo?.toLowerCase().includes(term) ||
            m.observacao?.toLowerCase().includes(term) ||
            m.tipo?.toLowerCase().includes(term) ||
            getNomeFornecedor(m.fornecedorId, m.produtoId).toLowerCase().includes(term) ||
            getNomeArmazem(m.armazemId).toLowerCase().includes(term) || // 🔥 ADICIONADO
            m._id?.toLowerCase().includes(term) ||
            formatarData(m.dataMovimento).toLowerCase().includes(term) ||
            formatarMoeda(m.valorTotal).toLowerCase().includes(term)
        );

        setFilteredMovimentos(resultado);

        setPagination(prev => ({
            ...prev,
            total: resultado.length,
            pages: Math.ceil(resultado.length / prev.limit),
            page: prev.page > Math.ceil(resultado.length / prev.limit) ? 1 : prev.page
        }));
    };

    const calcularStats = (movimentosList) => {
        if (!movimentosList || movimentosList.length === 0) {
            setStats({
                totalMovimentos: 0,
                totalEntradas: 0,
                totalSaidas: 0,
                totalAjustes: 0,
                totalEntradasValor: 0,
                totalSaidasValor: 0,
                topProdutos: []
            });
            return;
        }

        const total = movimentosList.length;
        const entradas = movimentosList.filter(m => m.tipo === 'entrada');
        const saidas = movimentosList.filter(m => m.tipo === 'saida');

        const totalEntradasValor = entradas.reduce((sum, m) => sum + (m.valorTotal || 0), 0);
        const totalSaidasValor = saidas.reduce((sum, m) => sum + (m.valorTotal || 0), 0);

        const produtoCount = {};
        movimentosList.forEach(m => {
            const id = m.produtoId._id;
            const nome = m.produtoId.nome;
            if (!produtoCount[id]) {
                produtoCount[id] = { nome, total: 0, count: 0 };
            }
            produtoCount[id].total += m.quantidade;
            produtoCount[id].count += 1;
        });

        const topProdutos = Object.values(produtoCount)
            .sort((a, b) => b.total - a.total)
            .slice(0, 5)
            .map(item => ({
                produto: item.nome,
                total: item.total,
                count: item.count
            }));

        setStats({
            totalMovimentos: total,
            totalEntradas: entradas.length,
            totalSaidas: saidas.length,
            totalAjustes: 0,
            totalEntradasValor: totalEntradasValor,
            totalSaidasValor: totalSaidasValor,
            topProdutos: topProdutos
        });
    };

    const fetchProdutos = async () => {
        try {
            const response = await api.get('/produtos');
            if (response.data && response.data.data) {
                setProdutos(response.data.data);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar produtos:', err);
        }
    };

    // ==============================================
    // HANDLE FILTROS
    // ==============================================
    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const aplicarFiltros = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchMovimentos();
    };

    const limparFiltros = () => {
        setFiltros({
            tipo: 'todos',
            produtoId: 'todos',
            armazem: 'todos',
            dataInicio: '',
            dataFim: ''
        });
        setShowFiltros(false);
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => fetchMovimentos(), 100);
    };

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const getCurrentPageMovimentos = () => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = startIndex + pagination.limit;
        return filteredMovimentos.slice(startIndex, endIndex);
    };

    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
    };

    // ==============================================
    // PAGINAÇÃO (CORRIGIDA - 2 PÁGINAS INÍCIO, "...", 2 PÁGINAS FIM)
    // ==============================================
    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        // 🔥 FUNÇÃO PARA GERAR OS NÚMEROS DE PÁGINA COM "..."
        const getPageNumbers = () => {
            // 🔥 Se o total de páginas for menor ou igual a 6, mostra todas
            if (pages <= 6) {
                return Array.from({ length: pages }, (_, i) => i + 1);
            }

            const delta = 2; // 🔥 Número de páginas antes e depois da atual
            const result = [];
            const range = [];
            const rangeWithDots = [];
            let l;

            // 🔥 Páginas à esquerda da página atual
            for (let i = 1; i <= pages; i++) {
                if (i === 1 || i === pages || (i >= page - delta && i <= page + delta)) {
                    range.push(i);
                }
            }

            // 🔥 Adicionar "..." entre os números
            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        const pageNumbers = getPageNumbers();

        return (
            <div className="movimentos-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} movimentos</span>
                </div>
                <div className="pagination-controls">
                    {/* 🔥 BOTÃO ANTERIOR */}
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1}
                        title="Página anterior"
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>

                    {/* 🔥 NÚMEROS DE PÁGINA */}
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => {
                            if (p === '...') {
                                return (
                                    <span key={`dots-${index}`} className="pagination-dots">
                                        …
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => goToPage(p)}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>

                    {/* 🔥 BOTÃO PRÓXIMO */}
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page + 1)}
                        disabled={page === pages}
                        title="Próxima página"
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // EFEITOS
    // ==============================================
    useEffect(() => {
        if (movimentos.length > 0) {
            aplicarPesquisaNaTabela(movimentos, searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        const carregarDados = async () => {
            await fetchLoggedUser();
            await Promise.all([
                fetchMovimentos(),
                fetchProdutos(),
                fetchFornecedores(),
                fetchArmazens() // 🔥 CARREGAR ARMAZÉNS
            ]);
        };
        carregarDados();
    }, []);

    useEffect(() => {
        if (empresaId) {
            fetchMovimentos();
        }
    }, [pagination.page, filtros, empresaId]);

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    const currentMovimentos = getCurrentPageMovimentos();

    return (
        <div className="movimentos-container">
            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* MODAL DE DETALHES */}
{showDetailsModal && movimentoSelecionado && (
    <div className="movimento-modal-overlay" onClick={() => setShowDetailsModal(false)}>
        <div className="movimento-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="movimento-modal-header">
                <h3>
                    <Icon name="visibility" size={22} />
                    Detalhes do Movimento
                </h3>
                <button className="movimento-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="movimento-modal-body">
                <div className="movimento-detalhes-grid">
                    <div className="movimento-info-item">
                        <span className="movimento-label">📌 Tipo</span>
                        <span className="movimento-value" style={{ color: getTipoColor(movimentoSelecionado.tipo) }}>
                            <Icon name={getTipoIcon(movimentoSelecionado.tipo)} size={16} />
                            {getTipoLabel(movimentoSelecionado.tipo)}
                        </span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">📦 Produto</span>
                        <span className="movimento-value">{movimentoSelecionado.produtoId?.nome || '-'}</span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">📊 Quantidade</span>
                        <span className="movimento-value">{movimentoSelecionado.quantidade} unidades</span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">💰 Preço Unitário</span>
                        <span className="movimento-value">{formatarMoeda(movimentoSelecionado.precoUnitario)}</span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">💵 Valor Total</span>
                        <span className="movimento-value">{formatarMoeda(movimentoSelecionado.valorTotal)}</span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">🏢 Fornecedor</span>
                        <span className="movimento-value">
                            {getNomeFornecedor(
                                movimentoSelecionado.fornecedorId,
                                movimentoSelecionado.produtoId
                            )}
                        </span>
                    </div>
                    {/* 🔥 NOVO CAMPO ARMAZÉM NO MODAL */}
                    <div className="movimento-info-item">
                        <span className="movimento-label">🏪 Armazém</span>
                        <span className="movimento-value">
                            {getNomeArmazem(movimentoSelecionado.armazemId)}
                        </span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">📅 Data</span>
                        <span className="movimento-value">{formatarData(movimentoSelecionado.dataMovimento)}</span>
                    </div>
                    <div className="movimento-info-item">
                        <span className="movimento-label">📄 Nota Fiscal</span>
                        <span className="movimento-value">{movimentoSelecionado.notaFiscal || '-'}</span>
                    </div>
                    <div className="movimento-info-item movimento-full-width">
                        <span className="movimento-label">📝 Observação</span>
                        <span className="movimento-value">{movimentoSelecionado.observacao || '-'}</span>
                    </div>
                </div>
                <div className="movimento-modal-actions">
                    <button className="movimento-btn-secondary" onClick={() => setShowDetailsModal(false)}>Fechar</button>
                </div>
            </div>
        </div>
    </div>
)}

            {/* HEADER */}
            <div className="movimentos-header">
                <div className="header-title">
                    <h2><Icon name="swap_horiz" size={24} /> Movimentos</h2>
                    <p>Histórico automático de todas as entradas e saídas de stock</p>
                </div>
            </div>

            {/* STATS */}
            {stats && (
                <div className="movimentos-stats">
                    <div className="stats-header">
                        <h4>📊 Resumo</h4>
                    </div>
                    <div className="stats-grid">
                        <div className="stat-card total">
                            <div className="stat-icon"><Icon name="swap_horiz" size={20} /></div>
                            <div className="stat-info">
                                <span className="stat-value">{stats.totalMovimentos || 0}</span>
                                <span className="stat-label">Total Movimentos</span>
                            </div>
                        </div>
                        <div className="stat-card entrada">
                            <div className="stat-icon"><Icon name="arrow_downward" size={20} /></div>
                            <div className="stat-info">
                                <span className="stat-value">{stats.totalEntradas || 0}</span>
                                <span className="stat-label">Entradas</span>
                                <span className="stat-sub">{formatarMoeda(stats.totalEntradasValor || 0)}</span>
                            </div>
                        </div>
                        <div className="stat-card saida">
                            <div className="stat-icon"><Icon name="arrow_upward" size={20} /></div>
                            <div className="stat-info">
                                <span className="stat-value">{stats.totalSaidas || 0}</span>
                                <span className="stat-label">Saídas</span>
                                <span className="stat-sub">{formatarMoeda(stats.totalSaidasValor || 0)}</span>
                            </div>
                        </div>
                    </div>

                    {stats.topProdutos && stats.topProdutos.length > 0 && (
                        <div className="top-produtos">
                            <h5>🏆 Produtos Mais Movimentados</h5>
                            <div className="top-produtos-list">
                                {stats.topProdutos.map((item, index) => (
                                    <div key={index} className="top-item">
                                        <span className="top-position">#{index + 1}</span>
                                        <span className="top-nome">{item.produto}</span>
                                        <span className="top-total">{item.total} un.</span>
                                        <span className="top-count">{item.count} mov.</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* FILTROS E PESQUISA */}
            <div className="movimentos-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder=" Pesquisar na tabela por produto, código, tipo, fornecedor, armazém..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <button className="btn-filter" onClick={() => setShowFiltros(!showFiltros)}>
                        <Icon name="filter_list" size={18} />
                        {showFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </button>
                    <button className="btn-refresh" onClick={fetchMovimentos} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* FILTROS AVANÇADOS COM ARMAZÉM */}
            {showFiltros && (
                <div className="movimentos-filtros-avancados">
                    <div className="filtros-row">
                        <div className="filtro-group">
                            <label>Tipo</label>
                            <select name="tipo" value={filtros.tipo} onChange={handleFiltroChange}>
                                <option value="todos">Todos</option>
                                <option value="entrada">Entrada</option>
                                <option value="saida">Saída</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Produto</label>
                            <select name="produtoId" value={filtros.produtoId} onChange={handleFiltroChange}>
                                <option value="todos">Todos</option>
                                {produtos.map(p => (
                                    <option key={p._id} value={p._id}>{p.nome}</option>
                                ))}
                            </select>
                        </div>
                        {/* 🔥 NOVO FILTRO DE ARMAZÉM */}
                        <div className="filtro-group">
                            <label>Armazém</label>
                            <select name="armazem" value={filtros.armazem} onChange={handleFiltroChange}>
                                <option value="todos">Todos os Armazéns</option>
                                {armazens.map(a => (
                                    <option key={a._id} value={a._id}>{a.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Data Início</label>
                            <input type="date" name="dataInicio" value={filtros.dataInicio} onChange={handleFiltroChange} />
                        </div>
                        <div className="filtro-group">
                            <label>Data Fim</label>
                            <input type="date" name="dataFim" value={filtros.dataFim} onChange={handleFiltroChange} />
                        </div>
                        <div className="filtro-actions">
                            <button className="btn-aplicar" onClick={aplicarFiltros}>
                                <Icon name="filter_list" size={16} /> Aplicar
                            </button>
                            <button className="btn-limpar" onClick={limparFiltros}>
                                <Icon name="clear" size={16} /> Limpar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TABELA DE MOVIMENTOS COM COLUNA ARMAZÉM */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar movimentos...</p>
                </div>
            ) : (
                <>
                    <div className="movimentos-table-container">
                        <table className="movimentos-table">
                            <thead>
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Tipo</th>
                                    <th>Produto</th>
                                    <th>Quantidade</th>
                                    <th>Preço Unit.</th>
                                    <th>Valor Total</th>
                                    <th>Fornecedor</th>
                                    <th>Armazém</th> {/* 🔥 NOVA COLUNA */}
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentMovimentos.length === 0 ? (
                                    <tr className="empty-row">
                                        <td colSpan="9">
                                            <div className="empty-state">
                                                <Icon name="swap_horiz" size={48} />
                                                <p>
                                                    {searchTerm ? 'Nenhum movimento encontrado com a pesquisa' : 'Nenhum movimento encontrado'}
                                                </p>
                                                <p className="sub-text">
                                                    {searchTerm
                                                        ? `Tente ajustar os termos da pesquisa: "${searchTerm}"`
                                                        : 'Os movimentos são registados automaticamente quando há entradas ou saídas de stock'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentMovimentos.map((mov, index) => (
                                        <tr key={mov._id || index}>
                                            <td>{formatarData(mov.dataMovimento)}</td>
                                            <td>
                                                <span className={`tipo-badge ${mov.tipo}`}>
                                                    <Icon name={getTipoIcon(mov.tipo)} size={12} />
                                                    {getTipoLabel(mov.tipo)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="produto-info">
                                                    <strong>{mov.produtoId?.nome || '-'}</strong>
                                                    {mov.produtoId?.codigo && (
                                                        <span className="codigo">{mov.produtoId.codigo}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{mov.quantidade} un.</td>
                                            <td>{formatarMoeda(mov.precoUnitario)}</td>
                                            <td>{formatarMoeda(mov.valorTotal)}</td>
                                            <td>
                                                {getNomeFornecedor(mov.fornecedorId, mov.produtoId)}
                                            </td>
                                            <td>
                                                <span className="armazem-badge">
                                                    {getNomeArmazem(mov.armazemId)}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="action-btn view"
                                                    onClick={() => {
                                                        setMovimentoSelecionado(mov);
                                                        setShowDetailsModal(true);
                                                    }}
                                                    title="Ver detalhes"
                                                >
                                                    <Icon name="visibility" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {renderPagination()}
                    </div>
                </>
            )}
        </div>
    );
}